/**
 * Bloque 5 — Atribución publicitaria persistente (cliente).
 *
 * Objetivo: que el clic de Google Ads (gclid/gbraid/wbraid + UTMs) sobreviva
 * desde la landing hasta la cuenta creada, sin PII y sin poder falsificar
 * pagos (el pago siempre lo confirma el webhook de Stripe).
 *
 * - Se captura de la URL en cada carga y se guarda una "instantánea" local.
 * - El first-touch NUNCA se sobrescribe (ni en local ni en base de datos).
 * - Al existir sesión de usuario se envía por RPC `record_user_acquisition`.
 */

import { supabase } from '@/integrations/supabase/client';
import { resolveLandingSessionId } from '@/lib/landingSession';

export const ATTRIBUTION_STORAGE_KEY = 'plaxter_attribution_first_touch';
export const ATTRIBUTION_LATEST_KEY = 'plaxter_attribution_latest_touch';

const MAX_VALUE_LENGTH = 512;

export interface AttributionSnapshot {
  landing_session_id?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_id?: string;
  referrer?: string;
  captured_at?: string;
}

const FIELDS = [
  'gclid',
  'gbraid',
  'wbraid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
] as const;

const sanitize = (value: string | null | undefined): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, MAX_VALUE_LENGTH);
};

/** Señales publicitarias reales (el referrer por sí solo no cuenta). */
export const hasAttributionSignal = (snapshot: AttributionSnapshot | null): boolean => {
  if (!snapshot) return false;
  return FIELDS.some((f) => Boolean(snapshot[f])) || Boolean(snapshot.landing_session_id);
};

/** Lee la atribución de la URL actual (sin tocar almacenamiento). */
export const readAttributionFromUrl = (
  search: string,
  referrer?: string,
  landingSessionId?: string,
): AttributionSnapshot => {
  const params = new URLSearchParams(search ?? '');
  const snapshot: AttributionSnapshot = {};
  for (const field of FIELDS) {
    const value = sanitize(params.get(field));
    if (value) snapshot[field] = value;
  }
  const session = sanitize(landingSessionId);
  if (session) snapshot.landing_session_id = session;
  const ref = sanitize(referrer);
  if (ref) snapshot.referrer = ref;
  return snapshot;
};

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* almacenamiento bloqueado */
  }
};

const parse = (raw: string | null): AttributionSnapshot | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as AttributionSnapshot;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Respaldo de primera parte del first-touch.
 *
 * Motivo: `localStorage` se pierde si el navegador lo limpia entre el clic del
 * anuncio y el alta. La cookie guarda EXACTAMENTE los mismos identificadores de
 * campaña (gclid/gbraid/wbraid/UTMs + sesión de landing). No contiene datos
 * personales, no se comparte con terceros y caduca a los 90 días.
 */
export const ATTRIBUTION_COOKIE_NAME = 'plaxter_ft';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;
const COOKIE_MAX_LENGTH = 1800;

const readCookie = (): AttributionSnapshot | null => {
  try {
    if (typeof document === 'undefined' || !document.cookie) return null;
    const entry = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${ATTRIBUTION_COOKIE_NAME}=`));
    if (!entry) return null;
    return parse(decodeURIComponent(entry.slice(ATTRIBUTION_COOKIE_NAME.length + 1)));
  } catch {
    return null;
  }
};

const writeCookie = (snapshot: AttributionSnapshot): void => {
  try {
    if (typeof document === 'undefined') return;
    // Sólo identificadores de campaña: nunca referrer ni nada personal.
    const payload: AttributionSnapshot = {};
    for (const field of FIELDS) {
      if (snapshot[field]) payload[field] = snapshot[field];
    }
    if (snapshot.landing_session_id) payload.landing_session_id = snapshot.landing_session_id;
    if (snapshot.captured_at) payload.captured_at = snapshot.captured_at;
    if (!hasAttributionSignal(payload)) return;
    const value = encodeURIComponent(JSON.stringify(payload));
    if (value.length > COOKIE_MAX_LENGTH) return;
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${ATTRIBUTION_COOKIE_NAME}=${value}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* cookies bloqueadas: se mantiene el mecanismo actual */
  }
};

export const getStoredFirstTouch = (): AttributionSnapshot | null => {
  const local = parse(safeGet(ATTRIBUTION_STORAGE_KEY));
  if (local) return local;
  const cookie = readCookie();
  if (cookie) {
    // Rehidrata el almacenamiento local para el resto de la sesión.
    safeSet(ATTRIBUTION_STORAGE_KEY, JSON.stringify(cookie));
    return cookie;
  }
  return null;
};

export const getStoredLatestTouch = (): AttributionSnapshot | null =>
  parse(safeGet(ATTRIBUTION_LATEST_KEY));

/**
 * Captura la atribución actual. Guarda el first-touch sólo la primera vez;
 * el latest-touch se actualiza cuando hay una señal publicitaria nueva.
 * Devuelve la instantánea que debe enviarse al servidor (first-touch).
 */
export const captureAttribution = (
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  referrer: string = typeof document !== 'undefined' ? document.referrer : '',
): AttributionSnapshot | null => {
  let sessionId = '';
  try {
    sessionId = resolveLandingSessionId(search);
  } catch {
    sessionId = '';
  }
  const current = readAttributionFromUrl(search, referrer, sessionId);
  const hasSignal = FIELDS.some((f) => Boolean(current[f]));

  const existingFirst = getStoredFirstTouch();
  if (!existingFirst && hasAttributionSignal(current)) {
    const snapshot = { ...current, captured_at: new Date().toISOString() };
    safeSet(ATTRIBUTION_STORAGE_KEY, JSON.stringify(snapshot));
    writeCookie(snapshot);
  }
  if (hasSignal) {
    safeSet(
      ATTRIBUTION_LATEST_KEY,
      JSON.stringify({ ...current, captured_at: new Date().toISOString() }),
    );
  }

  const stored = getStoredFirstTouch();
  // Respaldo idempotente: si el first-touch existía sólo en almacenamiento
  // local, asegura también la cookie (sin sobrescribir nada nuevo).
  if (stored && !readCookie()) writeCookie(stored);
  return stored;
};

/**
 * Persiste en base de datos el origen del usuario autenticado.
 * Idempotente y no bloqueante: un fallo nunca rompe registro ni pago.
 */
export const persistAttributionForCurrentUser = async (): Promise<boolean> => {
  const send = async (snapshot: AttributionSnapshot | null): Promise<boolean> => {
    if (!snapshot) return false;
    const payload: AttributionSnapshot = { ...snapshot };
    delete payload.captured_at;
    if (!hasAttributionSignal(payload)) return false;
    const { data, error } = await supabase.rpc('record_user_acquisition', {
      p_attribution: payload as unknown as never,
    });
    if (error) {
      console.warn('[attribution] record_user_acquisition failed', error.message);
      return false;
    }
    return data === true;
  };

  try {
    // 1) First-touch: crea la fila (el servidor nunca lo sobrescribe).
    const firstOk = await send(getStoredFirstTouch());
    // 2) Latest-touch: sólo actualiza las columnas de último origen.
    const latest = getStoredLatestTouch();
    const latestOk =
      latest && JSON.stringify(latest) !== JSON.stringify(getStoredFirstTouch())
        ? await send(latest)
        : false;
    return firstOk || latestOk;
  } catch (error) {
    console.warn('[attribution] persist threw (ignorable)', error);
    return false;
  }
};

